import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Download, Send, CheckCircle, XCircle, FileText, 
  Calendar, DollarSign, Clock, Award, Briefcase, Mail, Phone 
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const ProposalDetails = () => {
  const { proposalId } = useParams();
  const navigate = useNavigate();
  const [proposal, setProposal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchProposalDetails();
  }, [proposalId]);

  const fetchProposalDetails = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_BASE_URL}/api/proposals/${proposalId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setProposal(response.data);
    } catch (err) {
      console.error('Error fetching proposal:', err);
      setError('Failed to load proposal details');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = () => {
    if (!proposal) return;

    const proposalText = `
PROPOSAL DETAILS
================

Proposal Number: ${proposal.proposal_number || 'N/A'}
Trainer: ${proposal.trainer_details?.name || proposal.full_name || 'N/A'}
Email: ${proposal.trainer_details?.email || proposal.email || 'N/A'}
Phone: ${proposal.phone || 'N/A'}
Company: ${proposal.company || 'N/A'}
Status: ${proposal.status || 'N/A'}

PROGRAM TITLE
${proposal.proposal_title || proposal.title || 'N/A'}

TRAINING FORMAT
${proposal.training_format || 'N/A'}

TARGET AUDIENCE
${proposal.target_audience || 'N/A'}

LEARNING OBJECTIVES
${proposal.learning_objectives || 'N/A'}

METHODOLOGY
${proposal.methodology || 'N/A'}

PROPOSED TIMELINE
${proposal.proposed_timeline || 'N/A'}

KEY DELIVERABLES
${proposal.key_deliverables || 'N/A'}

PRICING BREAKDOWN
${proposal.pricing_breakdown || proposal.amount || 'N/A'}

TRAINER QUALIFICATIONS
Experience: ${proposal.years_experience || 'N/A'}
Certifications: ${proposal.certifications || 'N/A'}
Specializations: ${proposal.specializations || 'N/A'}

ADDITIONAL INFORMATION
${proposal.additional_info || proposal.description || 'N/A'}

Submitted: ${proposal.submitted_at ? new Date(proposal.submitted_at).toLocaleString() : new Date(proposal.created_at).toLocaleString()}
    `.trim();

    const blob = new Blob([proposalText], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `Proposal_${proposal.proposal_number || proposal.id}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const handleMessage = () => {
    navigate(`/messages/compose?to=${proposal.trainer_id}&proposalId=${proposalId}`);
  };

  const handleAccept = async () => {
    if (!window.confirm(`Are you sure you want to accept this proposal from ${proposal.trainer_details?.name || proposal.full_name}?`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_BASE_URL}/api/proposals/${proposalId}/status?status=accepted`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      alert('Proposal accepted successfully!');
      fetchProposalDetails();
    } catch (err) {
      console.error('Error accepting proposal:', err);
      alert('Failed to accept proposal');
    }
  };

  const handleReject = () => {
    const rejectionMessage = `Dear ${proposal.trainer_details?.name || proposal.full_name},

Thank you for submitting your proposal for "${proposal.proposal_title || 'our training opportunity'}".

After careful consideration, we regret to inform you that we will not be moving forward with your proposal at this time.

We appreciate the time and effort you put into your submission.

Best regards,
[Your Organization]`;

    navigate(`/messages/compose?to=${proposal.trainer_id}&proposalId=${proposalId}&action=reject&message=${encodeURIComponent(rejectionMessage)}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (error || !proposal) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Card className="w-full max-w-md">
          <CardContent className="p-12 text-center">
            <FileText className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <p className="text-gray-900 font-semibold mb-2">Failed to load proposal</p>
            <p className="text-gray-600 mb-4">{error}</p>
            <Button onClick={() => navigate(-1)} variant="outline">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Go Back
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <Button variant="ghost" onClick={() => navigate(-1)} className="flex items-center">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Proposals
        </Button>
        <div className="flex items-center space-x-2">
          <Button variant="outline" onClick={handleDownload}>
            <Download className="w-4 h-4 mr-2" />
            Download
          </Button>
          <Button variant="outline" onClick={handleMessage}>
            <Send className="w-4 h-4 mr-2" />
            Message
          </Button>
          {proposal.status !== 'rejected' && (
            <Button variant="outline" onClick={handleReject} className="text-red-600 hover:bg-red-50">
              <XCircle className="w-4 h-4 mr-2" />
              Reject
            </Button>
          )}
          {proposal.status !== 'accepted' && (
            <Button onClick={handleAccept} className="bg-green-600 hover:bg-green-700">
              <CheckCircle className="w-4 h-4 mr-2" />
              Accept
            </Button>
          )}
        </div>
      </div>

      {/* Proposal Header Card */}
      <Card>
        <CardContent className="p-6">
          <div className="flex justify-between items-start mb-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                {proposal.proposal_title || proposal.title}
              </h1>
              <p className="text-gray-600">Proposal #{proposal.proposal_number}</p>
            </div>
            <Badge className={`text-base px-4 py-2 ${
              proposal.status === 'sent' ? 'bg-orange-100 text-orange-800' :
              proposal.status === 'accepted' ? 'bg-green-100 text-green-800' :
              proposal.status === 'rejected' ? 'bg-red-100 text-red-800' :
              'bg-gray-100 text-gray-800'
            }`}>
              {proposal.status === 'sent' ? 'Pending Review' : proposal.status || 'Pending'}
            </Badge>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div className="flex items-center space-x-3">
              <DollarSign className="w-5 h-5 text-green-600" />
              <div>
                <p className="text-xs text-gray-500">Proposed Amount</p>
                <p className="font-semibold text-lg">{proposal.amount}</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <Calendar className="w-5 h-5 text-blue-600" />
              <div>
                <p className="text-xs text-gray-500">Submitted</p>
                <p className="font-semibold">
                  {proposal.submitted_at 
                    ? new Date(proposal.submitted_at).toLocaleDateString()
                    : new Date(proposal.created_at).toLocaleDateString()
                  }
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <Clock className="w-5 h-5 text-purple-600" />
              <div>
                <p className="text-xs text-gray-500">Timeline</p>
                <p className="font-semibold">{proposal.proposed_timeline || 'See details'}</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Trainer Information */}
      <Card>
        <CardHeader>
          <CardTitle>Trainer Information</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-start space-x-6">
            {proposal.trainer_details?.avatar ? (
              <img 
                src={proposal.trainer_details.avatar} 
                alt={proposal.trainer_details.name}
                className="w-24 h-24 rounded-full"
              />
            ) : (
              <div className="w-24 h-24 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-bold text-3xl">
                {(proposal.trainer_details?.name || proposal.full_name || 'T')[0].toUpperCase()}
              </div>
            )}
            <div className="flex-1">
              <h3 className="text-2xl font-bold text-gray-900 mb-2">
                {proposal.trainer_details?.name || proposal.full_name || 'Unknown'}
              </h3>
              <p className="text-gray-600 mb-4">{proposal.trainer_details?.title || proposal.years_experience}</p>
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center space-x-2">
                  <Mail className="w-4 h-4 text-gray-500" />
                  <span className="text-sm text-gray-700">{proposal.email}</span>
                </div>
                {proposal.phone && (
                  <div className="flex items-center space-x-2">
                    <Phone className="w-4 h-4 text-gray-500" />
                    <span className="text-sm text-gray-700">{proposal.phone}</span>
                  </div>
                )}
                {proposal.company && (
                  <div className="flex items-center space-x-2">
                    <Briefcase className="w-4 h-4 text-gray-500" />
                    <span className="text-sm text-gray-700">{proposal.company}</span>
                  </div>
                )}
                {proposal.years_experience && (
                  <div className="flex items-center space-x-2">
                    <Award className="w-4 h-4 text-gray-500" />
                    <span className="text-sm text-gray-700">{proposal.years_experience}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Proposal Details */}
      <Card>
        <CardHeader>
          <CardTitle>Proposal Details</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {proposal.training_format && (
            <div>
              <h4 className="font-semibold text-gray-900 mb-2">Training Format</h4>
              <p className="text-gray-700">{proposal.training_format}</p>
            </div>
          )}

          {proposal.target_audience && (
            <div>
              <h4 className="font-semibold text-gray-900 mb-2">Target Audience</h4>
              <p className="text-gray-700">{proposal.target_audience}</p>
            </div>
          )}

          {proposal.learning_objectives && (
            <div>
              <h4 className="font-semibold text-gray-900 mb-2">Learning Objectives</h4>
              <p className="text-gray-700 whitespace-pre-wrap">{proposal.learning_objectives}</p>
            </div>
          )}

          {proposal.methodology && (
            <div>
              <h4 className="font-semibold text-gray-900 mb-2">Methodology</h4>
              <p className="text-gray-700 whitespace-pre-wrap">{proposal.methodology}</p>
            </div>
          )}

          {proposal.assessment_methods && (
            <div>
              <h4 className="font-semibold text-gray-900 mb-2">Assessment Methods</h4>
              <p className="text-gray-700 whitespace-pre-wrap">{proposal.assessment_methods}</p>
            </div>
          )}

          {proposal.key_deliverables && (
            <div>
              <h4 className="font-semibold text-gray-900 mb-2">Key Deliverables</h4>
              <p className="text-gray-700 whitespace-pre-wrap">{proposal.key_deliverables}</p>
            </div>
          )}

          {proposal.post_training_support && (
            <div>
              <h4 className="font-semibold text-gray-900 mb-2">Post-Training Support</h4>
              <p className="text-gray-700 whitespace-pre-wrap">{proposal.post_training_support}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pricing Details */}
      <Card>
        <CardHeader>
          <CardTitle>Pricing & Commercial Terms</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {proposal.pricing_breakdown && (
            <div>
              <h4 className="font-semibold text-gray-900 mb-2">Pricing Breakdown</h4>
              <p className="text-gray-700 whitespace-pre-wrap">{proposal.pricing_breakdown}</p>
            </div>
          )}

          {proposal.included_materials && (
            <div>
              <h4 className="font-semibold text-gray-900 mb-2">Included Materials</h4>
              <p className="text-gray-700 whitespace-pre-wrap">{proposal.included_materials}</p>
            </div>
          )}

          {proposal.travel_costs && (
            <div>
              <h4 className="font-semibold text-gray-900 mb-2">Travel Costs</h4>
              <p className="text-gray-700">{proposal.travel_costs}</p>
            </div>
          )}

          {proposal.cancellation_policy && (
            <div>
              <h4 className="font-semibold text-gray-900 mb-2">Cancellation Policy</h4>
              <p className="text-gray-700 whitespace-pre-wrap">{proposal.cancellation_policy}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Qualifications */}
      <Card>
        <CardHeader>
          <CardTitle>Trainer Qualifications & Experience</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {proposal.certifications && (
            <div>
              <h4 className="font-semibold text-gray-900 mb-2">Certifications</h4>
              <p className="text-gray-700">{proposal.certifications}</p>
            </div>
          )}

          {proposal.specializations && (
            <div>
              <h4 className="font-semibold text-gray-900 mb-2">Specializations</h4>
              <p className="text-gray-700">{proposal.specializations}</p>
            </div>
          )}

          {proposal.previous_experience && (
            <div>
              <h4 className="font-semibold text-gray-900 mb-2">Relevant Experience</h4>
              <p className="text-gray-700 whitespace-pre-wrap">{proposal.previous_experience}</p>
            </div>
          )}

          {proposal.languages_offered && (
            <div>
              <h4 className="font-semibold text-gray-900 mb-2">Languages Offered</h4>
              <p className="text-gray-700">{proposal.languages_offered}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Attachments */}
      {(proposal.resume || proposal.credentials || proposal.sample_materials || proposal.testimonials || proposal.references) && (
        <Card>
          <CardHeader>
            <CardTitle>Attachments</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {proposal.resume && (
                <div className="flex items-center justify-between p-3 border rounded-lg">
                  <div className="flex items-center space-x-3">
                    <FileText className="w-5 h-5 text-blue-600" />
                    <span className="font-medium">Resume / CV</span>
                  </div>
                  <Button size="sm" variant="outline">
                    <Download className="w-4 h-4 mr-2" />
                    Download
                  </Button>
                </div>
              )}
              {proposal.credentials && (
                <div className="flex items-center justify-between p-3 border rounded-lg">
                  <div className="flex items-center space-x-3">
                    <FileText className="w-5 h-5 text-green-600" />
                    <span className="font-medium">Credentials & Certificates</span>
                  </div>
                  <Button size="sm" variant="outline">
                    <Download className="w-4 h-4 mr-2" />
                    Download
                  </Button>
                </div>
              )}
              {proposal.sample_materials && (
                <div className="flex items-center justify-between p-3 border rounded-lg">
                  <div className="flex items-center space-x-3">
                    <FileText className="w-5 h-5 text-purple-600" />
                    <span className="font-medium">Sample Training Materials</span>
                  </div>
                  <Button size="sm" variant="outline">
                    <Download className="w-4 h-4 mr-2" />
                    Download
                  </Button>
                </div>
              )}
              {proposal.testimonials && (
                <div className="flex items-center justify-between p-3 border rounded-lg">
                  <div className="flex items-center space-x-3">
                    <FileText className="w-5 h-5 text-yellow-600" />
                    <span className="font-medium">Client Testimonials</span>
                  </div>
                  <Button size="sm" variant="outline">
                    <Download className="w-4 h-4 mr-2" />
                    Download
                  </Button>
                </div>
              )}
              {proposal.references && (
                <div className="flex items-center justify-between p-3 border rounded-lg">
                  <div className="flex items-center space-x-3">
                    <FileText className="w-5 h-5 text-orange-600" />
                    <span className="font-medium">References</span>
                  </div>
                  <Button size="sm" variant="outline">
                    <Download className="w-4 h-4 mr-2" />
                    Download
                  </Button>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Additional Information */}
      {proposal.additional_info && (
        <Card>
          <CardHeader>
            <CardTitle>Additional Information</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-700 whitespace-pre-wrap">{proposal.additional_info}</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ProposalDetails;
